/**
 * Recherche globale (🔍) : élèves, cours, cours privés, festivals, événements, tâches.
 * Mobile : plein écran. PC : fenêtre centrée (raccourci ⌘K).
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Search, X, Phone, MessageCircle, BookOpen, UserRound, Ticket, CalendarDays, CheckSquare, Clock, ChevronRight } from 'lucide-react';
import { api } from '../api';
import { cn } from '../lib/utils';
import { Avatar } from '../pages/Dashboard';
import { Chip, BanknotePlus, parseAmount, fmtAmount } from './MobileHeader';

const SCOPES = [
  ['all', 'Tout'], ['students', 'Élèves'], ['courses', 'Cours'], ['private_classes', 'Privés'],
  ['festivals', 'Festivals'], ['events', 'Événements'], ['tasks', 'Tâches'],
];
const DAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const RECENT_KEY = 'ds_recent_searches';

const readRecent = () => { try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); } catch { return []; } };
const saveRecent = (q) => {
  try {
    const list = [q, ...readRecent().filter(x => x !== q)].slice(0, 6);
    localStorage.setItem(RECENT_KEY, JSON.stringify(list));
  } catch { /* stockage indisponible : on ignore */ }
};
const fmt = (iso, pattern) => { try { return format(parseISO(iso), pattern, { locale: fr }); } catch { return ''; } };
const waLink = (phone) => {
  let d = (phone || '').replace(/\D/g, '');
  if (d.startsWith('0')) d = '41' + d.slice(1);
  return `https://wa.me/${d}`;
};

export default function GlobalSearch({ open, onClose, initialQuery = '', initialScope = 'all', isAdmin, pendingCount = 0, onCashbox }) {
  const navigate = useNavigate();
  const [q, setQ] = useState(initialQuery);
  const [scope, setScope] = useState(initialScope);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const reqId = useRef(0);

  useEffect(() => { if (open) { setQ(initialQuery); setScope(initialScope); } }, [open, initialQuery, initialScope]);

  useEffect(() => {
    if (!open) return;
    const query = q.trim();
    if (query.length < 2) { setResults(null); return; }
    const id = ++reqId.current;
    setLoading(true);
    const t = setTimeout(() => {
      api.get(`/search?q=${encodeURIComponent(query)}&scope=${scope}`)
        .then(r => { if (id === reqId.current) setResults(r); })
        .catch(() => { if (id === reqId.current) setResults(null); })
        .finally(() => { if (id === reqId.current) setLoading(false); });
    }, 200);
    return () => clearTimeout(t);
  }, [q, scope, open]);

  if (!open) return null;

  const go = (path, state) => { if (q.trim()) saveRecent(q.trim()); onClose(); navigate(path, state ? { state } : undefined); };
  const scopes = isAdmin ? SCOPES : SCOPES.filter(([k]) => ['all', 'students', 'courses'].includes(k));
  const total = results ? Object.values(results).reduce((n, l) => n + l.length, 0) : 0;

  const Section = ({ title, icon: Icon, children, count }) => count ? (
    <div className="mb-4">
      <div className="flex items-center gap-1.5 px-1 mb-1.5 text-xs font-bold uppercase tracking-wide text-slate-400">
        <Icon size={13} /> {title}
      </div>
      <div className="bg-white rounded-2xl border border-slate-100 divide-y divide-slate-50 overflow-hidden">{children}</div>
    </div>
  ) : null;
  const Row = ({ onClick, children, testId }) => (
    <button onClick={onClick} data-testid={testId}
      className="w-full flex items-center gap-3 px-3 py-2.5 text-left active:bg-slate-50 hover:bg-slate-50">
      {children}
      <ChevronRight size={16} className="text-slate-300 shrink-0" />
    </button>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/30 lg:flex lg:items-start lg:justify-center lg:pt-24" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} data-testid="global-search"
        className="w-full h-full lg:h-auto lg:max-h-[75vh] lg:max-w-2xl bg-slate-50 lg:rounded-2xl lg:shadow-2xl flex flex-col"
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        {/* Champ de recherche */}
        <div className="bg-white border-b border-slate-100 lg:rounded-t-2xl">
          <div className="h-14 flex items-center gap-2 px-3">
            <Search size={18} className="text-slate-400 shrink-0" />
            <input autoFocus value={q} onChange={e => setQ(e.target.value)} data-testid="global-search-input"
              placeholder={isAdmin ? "Nom, téléphone, cours… ou un montant" : "Nom, cours…"}
              className="flex-1 min-w-0 h-10 bg-transparent text-base text-slate-900 outline-none" />
            <button onClick={onClose} aria-label="Fermer" className="w-10 h-10 flex items-center justify-center rounded-xl text-slate-500 active:bg-slate-100">
              <X size={20} />
            </button>
          </div>
          {/* Actions : un montant tapé propose « Encaisser » ; champ vide → paiements à attribuer */}
          {isAdmin && (parseAmount(q) || (!q.trim() && pendingCount > 0)) && (
            <div className="flex gap-2 px-3 pb-2.5">
              {parseAmount(q) && (
                <button onClick={() => onCashbox(parseAmount(q))} data-testid="search-cashbox-btn"
                  className="h-10 px-4 rounded-full bg-[#26A69A] text-white text-sm font-bold flex items-center gap-1.5">
                  <BanknotePlus size={16} /> <span className="ml-1">Encaisser {fmtAmount(parseAmount(q))} CHF</span>
                </button>
              )}
              {!q.trim() && pendingCount > 0 && (
                <button onClick={() => onCashbox(null, true)} data-testid="search-pending-btn"
                  className="h-10 px-4 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-sm font-semibold">
                  À attribuer ({pendingCount})
                </button>
              )}
            </div>
          )}
          <div className="flex gap-2 px-3 pb-2.5 overflow-x-auto no-scrollbar">
            {scopes.map(([k, label]) => (
              <Chip key={k} active={scope === k} onClick={() => setScope(k)} color="#334155">{label}</Chip>
            ))}
          </div>
        </div>

        {/* Résultats */}
        <div className="flex-1 overflow-y-auto p-3" style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))' }}>
          {q.trim().length < 2 ? (
            readRecent().length > 0 && (
              <div>
                <div className="text-xs font-bold uppercase tracking-wide text-slate-400 px-1 mb-2">Recherches récentes</div>
                <div className="flex flex-wrap gap-2">
                  {readRecent().map(r => <Chip key={r} icon={Clock} onClick={() => setQ(r)}>{r}</Chip>)}
                </div>
              </div>
            )
          ) : !results ? (
            <div className="text-center py-10 text-slate-400 text-sm">{loading ? 'Recherche…' : ''}</div>
          ) : total === 0 ? (
            isAdmin && parseAmount(q) ? null : <div className="text-center py-10 text-slate-400 text-sm">Aucun résultat pour « {q.trim()} »</div>
          ) : (
            <>
              <Section title="Élèves" icon={UserRound} count={results.students.length}>
                {results.students.map(s => (
                  <div key={s.id} className="flex items-center">
                    <Row onClick={() => go(`/students/${s.id}`)} testId="search-result-student">
                      <Avatar name={`${s.first_name} ${s.last_name}`} size="sm" />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-900 truncate">{s.first_name} {s.last_name}</div>
                        <div className="text-xs text-slate-400 truncate">
                          {[s.role === 'leader' ? 'Leader' : s.role === 'follower' ? 'Follower' : '', s.phone, s.instagram && `@${s.instagram.replace(/^@/, '')}`].filter(Boolean).join(' · ')}
                        </div>
                      </div>
                    </Row>
                    {s.phone && (
                      <div className="flex shrink-0 pr-2 gap-1">
                        <a href={`tel:${s.phone.replace(/\s/g, '')}`} aria-label="Appeler" className="w-9 h-9 flex items-center justify-center rounded-lg bg-slate-50 text-slate-500"><Phone size={16} /></a>
                        <a href={waLink(s.phone)} target="_blank" rel="noreferrer" aria-label="WhatsApp" className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#25D366]/10 text-[#25D366]"><MessageCircle size={16} /></a>
                      </div>
                    )}
                  </div>
                ))}
              </Section>
              <Section title="Cours collectifs" icon={BookOpen} count={results.courses.length}>
                {results.courses.map(c => (
                  <Row key={c.id} onClick={() => go(`/group-classes/${c.id}`)} testId="search-result-course">
                    <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: c.color || '#0288D1' }} />
                    <div className="flex-1 min-w-0">
                      <div className={cn('font-semibold truncate', c.is_active === false ? 'text-slate-400' : 'text-slate-900')}>{c.name}</div>
                      <div className="text-xs text-slate-400 truncate">{DAYS[c.day_of_week]} {c.start_time} · {c.venue}{c.is_active === false ? ' · inactif' : ''}</div>
                    </div>
                  </Row>
                ))}
              </Section>
              <Section title="Cours privés" icon={UserRound} count={results.private_classes.length}>
                {results.private_classes.map(c => (
                  <Row key={c.id} onClick={() => go('/planning', { focusDate: c.datetime_start, openEditFor: c.id })}>
                    <span className="w-3 h-3 rounded-full shrink-0 bg-[#26A69A]" />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-900 truncate">{c.title}</div>
                      <div className="text-xs text-slate-400 capitalize">{fmt(c.datetime_start, "EEEE d MMMM yyyy · HH:mm")}</div>
                    </div>
                  </Row>
                ))}
              </Section>
              <Section title="Festivals" icon={Ticket} count={results.festivals.length}>
                {results.festivals.map(f => (
                  <Row key={f.id} onClick={() => go('/festivals')}>
                    <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: f.color || '#F59E0B' }} />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-900 truncate">{f.name}</div>
                      <div className="text-xs text-slate-400 truncate">{fmt(f.date_start, 'd MMM')} → {fmt(f.date_end, 'd MMM yyyy')}{f.city ? ` · ${f.city}` : ''}</div>
                    </div>
                  </Row>
                ))}
              </Section>
              <Section title="Événements" icon={CalendarDays} count={results.events.length}>
                {results.events.map(e => (
                  <Row key={e.id} onClick={() => go('/planning', { focusDate: e.datetime_start })}>
                    <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: e.color || '#607D8B' }} />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-900 truncate">{e.title}</div>
                      <div className="text-xs text-slate-400 capitalize">{fmt(e.datetime_start, "EEEE d MMMM yyyy · HH:mm")}</div>
                    </div>
                  </Row>
                ))}
              </Section>
              <Section title="Tâches" icon={CheckSquare} count={results.tasks.length}>
                {results.tasks.map(t => (
                  <Row key={t.id} onClick={() => go('/habits', { taskQuery: t.title })}>
                    <span className="w-3 h-3 rounded shrink-0" style={{ backgroundColor: t.list_color || '#94a3b8' }} />
                    <div className="flex-1 min-w-0">
                      <div className={cn('font-semibold truncate', t.completed ? 'line-through text-slate-400' : 'text-slate-900')}>{t.title}</div>
                      <div className="text-xs text-slate-400 truncate">{t.list_name}{t.completed ? ' · terminée' : ''}{t.description ? ` · ${t.description}` : ''}</div>
                    </div>
                  </Row>
                ))}
              </Section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
